import { createGenericStore } from '@/stores/utils/genericStore.js'
import { numericId } from './chatStoreUtils.js'

const normalizePresenceUser = (user) => {
  const userId = numericId(user?.id ?? user?.user_id ?? user)

  if (!Number.isFinite(userId) || userId <= 0) {
    return null
  }

  return typeof user === 'object'
    ? { ...user, id: userId }
    : { id: userId }
}

const presenceUserStoreOptions = {
  state: () => ({
    usersById: {},
    globalOnlineUserIds: [],
    onlineUserIdsByConversationId: {}
  }),

  getters: {
    getOnlineUserIdsByConversationId(state) {
      return (conversationId) => state.onlineUserIdsByConversationId[numericId(conversationId)] ?? []
    },

    getOnlineUsersByConversationId(state) {
      return (conversationId) => {
        const userIds = state.onlineUserIdsByConversationId[numericId(conversationId)] ?? []

        return userIds
          .map((userId) => state.usersById[userId])
          .filter(Boolean)
      }
    },

    onlineUsers(state) {
      const userIds = [
        ...new Set([
          ...state.globalOnlineUserIds,
          ...Object.values(state.onlineUserIdsByConversationId).flat()
        ])
      ]

      return userIds
        .map((userId) => state.usersById[numericId(userId)])
        .filter(Boolean)
    },

    isUserOnlineInConversation(state) {
      return (conversationId, userId) => {
        const userIds = state.onlineUserIdsByConversationId[numericId(conversationId)] ?? []

        return userIds.includes(numericId(userId))
      }
    }
  },

  actions: {
    patchUserState(user) {
      const normalizedUser = normalizePresenceUser(user)

      if (!normalizedUser) {
        return
      }

      this.usersById = {
        ...this.usersById,
        [normalizedUser.id]: {
          ...(this.usersById[normalizedUser.id] ?? {}),
          ...normalizedUser
        }
      }
    },

    setGlobalUsers(users = []) {
      const normalizedUsers = users
        .map((user) => normalizePresenceUser(user))
        .filter(Boolean)

      for (const user of normalizedUsers) {
        this.patchUserState(user)
      }

      this.globalOnlineUserIds = normalizedUsers.map((user) => user.id)
    },

    markGlobalUserOnline(user) {
      const normalizedUser = normalizePresenceUser(user)

      if (!normalizedUser) {
        return
      }

      this.patchUserState(normalizedUser)

      if (this.globalOnlineUserIds.includes(normalizedUser.id)) {
        return
      }

      this.globalOnlineUserIds = [...this.globalOnlineUserIds, normalizedUser.id]
    },

    markGlobalUserOffline(user) {
      const normalizedUser = normalizePresenceUser(user)

      if (!normalizedUser) {
        return
      }

      this.globalOnlineUserIds = this.globalOnlineUserIds.filter((userId) => userId !== normalizedUser.id)
    },

    clearGlobalPresence() {
      this.globalOnlineUserIds = []
    },

    setConversationUsers(conversationId, users = []) {
      const normalizedConversationId = numericId(conversationId)
      const normalizedUsers = users
        .map((user) => normalizePresenceUser(user))
        .filter(Boolean)

      for (const user of normalizedUsers) {
        this.patchUserState(user)
      }

      this.onlineUserIdsByConversationId = {
        ...this.onlineUserIdsByConversationId,
        [normalizedConversationId]: normalizedUsers.map((user) => user.id)
      }
    },

    markUserOnline(conversationId, user) {
      const normalizedConversationId = numericId(conversationId)
      const normalizedUser = normalizePresenceUser(user)

      if (!normalizedUser) {
        return
      }

      const currentUserIds = this.onlineUserIdsByConversationId[normalizedConversationId] ?? []

      this.patchUserState(normalizedUser)

      if (currentUserIds.includes(normalizedUser.id)) {
        return
      }

      this.onlineUserIdsByConversationId = {
        ...this.onlineUserIdsByConversationId,
        [normalizedConversationId]: [...currentUserIds, normalizedUser.id]
      }
    },

    markUserOffline(conversationId, user) {
      const normalizedConversationId = numericId(conversationId)
      const normalizedUser = normalizePresenceUser(user)

      if (!normalizedUser) {
        return
      }

      const currentUserIds = this.onlineUserIdsByConversationId[normalizedConversationId] ?? []

      this.onlineUserIdsByConversationId = {
        ...this.onlineUserIdsByConversationId,
        [normalizedConversationId]: currentUserIds.filter((userId) => userId !== normalizedUser.id)
      }
    },

    clearConversationPresence(conversationId) {
      const normalizedConversationId = numericId(conversationId)
      const {
        [normalizedConversationId]: removedConversation,
        ...remainingConversationPresence
      } = this.onlineUserIdsByConversationId

      void removedConversation

      this.onlineUserIdsByConversationId = remainingConversationPresence
    },

    clearAllPresence() {
      this.globalOnlineUserIds = []
      this.onlineUserIdsByConversationId = {}
      this.usersById = {}
    }
  }
}

export const usePresenceUserStore = createGenericStore(
  'chat-presence-users',
  '/api/v1/conversations',
  presenceUserStoreOptions
)
